import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Microscope, Plus, RefreshCw, FlaskConical, Bookmark, EyeOff, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface Signal {
  id: string;
  signal_type: string;
  relevance_level: string;
  title: string;
  description: string;
  source: string | null;
  cosmo_take: string | null;
  status: string;
  experiment_id: string | null;
  created_at: string;
}

// ── Constants ─────────────────────────────────────────────────────

const RELEVANCE_META = {
  high:        { label: 'Test This',   color: 'bg-green-500/10 text-green-600 border-green-500/30',   dot: 'bg-green-500' },
  medium:      { label: 'Interesting', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30', dot: 'bg-yellow-500' },
  observation: { label: 'Observation', color: 'bg-blue-500/10 text-blue-500 border-blue-500/30',       dot: 'bg-blue-400' },
};

const TYPE_LABELS: Record<string, string> = {
  external:     'External research',
  internal:     'Internal pattern',
  cross_client: 'Cross-client pattern',
};

// ── Main component ────────────────────────────────────────────────

export default function ResearchFeed() {
  const navigate = useNavigate();

  const [signals, setSignals]   = useState<Signal[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filter, setFilter]     = useState<'all' | 'new' | 'saved'>('all');

  // Add dialog
  const [showAdd, setShowAdd]     = useState(false);
  const [form, setForm]           = useState({ title: '', description: '', source: '', cosmo_take: '', signal_type: 'external', relevance_level: 'medium' });
  const [addSaving, setAddSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('research_signals')
      .select('*')
      .neq('status', 'dismissed')
      .order('relevance_level')
      .order('created_at', { ascending: false });
    if (error) toast.error(error.message);
    setSignals(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });

  const updateStatus = async (id: string, newStatus: string) => {
    const { error } = await supabase.from('research_signals').update({ status: newStatus }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    const labels: Record<string, string> = { saved: 'Saved to watchlist', dismissed: 'Dismissed', experiment_created: 'Marked as tested' };
    toast.success(labels[newStatus] ?? 'Updated');
    load();
  };

  const addSignal = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error('Title and description are required'); return; }
    setAddSaving(true);
    const { error } = await supabase.from('research_signals').insert({
      title: form.title.trim(),
      description: form.description.trim(),
      source: form.source.trim() || null,
      cosmo_take: form.cosmo_take.trim() || null,
      signal_type: form.signal_type,
      relevance_level: form.relevance_level,
      status: 'new',
    });
    setAddSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Signal added');
    setShowAdd(false);
    setForm({ title: '', description: '', source: '', cosmo_take: '', signal_type: 'external', relevance_level: 'medium' });
    load();
  };

  const createExperiment = (s: Signal) => {
    updateStatus(s.id, 'experiment_created');
    navigate(`/admin/growth/experiments?from_signal=${s.id}`);
  };

  // Filter
  const filtered = signals.filter(s => {
    if (filter === 'new')   return s.status === 'new';
    if (filter === 'saved') return s.status === 'saved';
    return true;
  });

  const groups: { key: string; items: Signal[] }[] = [
    { key: 'high',        items: filtered.filter(s => s.relevance_level === 'high') },
    { key: 'medium',      items: filtered.filter(s => s.relevance_level === 'medium') },
    { key: 'observation', items: filtered.filter(s => s.relevance_level === 'observation') },
  ].filter(g => g.items.length > 0);

  const SignalCard = ({ signal }: { signal: Signal }) => {
    const meta   = RELEVANCE_META[signal.relevance_level as keyof typeof RELEVANCE_META] ?? RELEVANCE_META.medium;
    const isOpen = expanded.has(signal.id);

    return (
      <Card className="overflow-hidden">
        <div
          className="flex items-start gap-3 p-4 cursor-pointer hover:bg-muted/20"
          onClick={() => toggleExpand(signal.id)}
        >
          <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${meta.dot}`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs text-muted-foreground">{TYPE_LABELS[signal.signal_type] ?? signal.signal_type}</span>
              {signal.status === 'saved' && <Badge variant="outline" className="text-xs">Saved</Badge>}
              {signal.status === 'experiment_created' && <Badge variant="outline" className="text-xs text-green-600">Tested</Badge>}
            </div>
            <p className="font-medium text-sm">{signal.title}</p>
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{signal.description}</p>
          </div>
          <div className="shrink-0">
            {isOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>

        {isOpen && (
          <div className="border-t border-border/50 px-4 pb-4 pt-3 space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Details</p>
              <p className="text-sm text-muted-foreground">{signal.description}</p>
            </div>
            {signal.source && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Source</p>
                <p className="text-sm text-muted-foreground">{signal.source}</p>
              </div>
            )}
            {signal.cosmo_take && (
              <div className="bg-muted/30 rounded-lg p-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Cosmo's take</p>
                <p className="text-sm">{signal.cosmo_take}</p>
              </div>
            )}
            <Separator />
            <div className="flex flex-wrap gap-2">
              {signal.relevance_level === 'high' && signal.status !== 'experiment_created' && (
                <Button size="sm" onClick={() => createExperiment(signal)}>
                  <FlaskConical className="h-3.5 w-3.5 mr-1.5" />Design Experiment
                </Button>
              )}
              {signal.status === 'new' && (
                <Button size="sm" variant="outline" onClick={() => updateStatus(signal.id, 'saved')}>
                  <Bookmark className="h-3.5 w-3.5 mr-1.5" />Save for later
                </Button>
              )}
              {signal.relevance_level !== 'high' && signal.status !== 'experiment_created' && (
                <Button size="sm" variant="outline" onClick={() => createExperiment(signal)}>
                  <FlaskConical className="h-3.5 w-3.5 mr-1.5" />Turn into experiment
                </Button>
              )}
              <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => updateStatus(signal.id, 'dismissed')}>
                <EyeOff className="h-3.5 w-3.5 mr-1.5" />Dismiss
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Added {formatDistanceToNow(new Date(signal.created_at), { addSuffix: true })}</p>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Research & Signal Feed</h1>
          <p className="text-sm text-muted-foreground mt-0.5">External research, internal patterns, and cross-client signals filtered for relevance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />Add Signal
          </Button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2">
        {(['all', 'new', 'saved'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
          >
            {f === 'all' ? 'All' : f === 'new' ? 'New' : 'Saved'}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Microscope className="h-5 w-5 animate-pulse mr-2" /><span className="text-sm">Loading signals…</span>
        </div>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Microscope className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium mb-1">No signals yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Add research findings, cross-client patterns, or behavioral science insights that could improve client engagement.
            </p>
            <Button onClick={() => setShowAdd(true)}><Plus className="h-4 w-4 mr-1.5" />Add Signal</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groups.map(group => {
            const meta = RELEVANCE_META[group.key];
            return (
              <div key={group.key}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full ${meta.dot}`} />
                  <h2 className="text-sm font-semibold uppercase tracking-wide">{meta.label}</h2>
                  <Badge variant="outline" className={`text-xs ${meta.color}`}>{group.items.length}</Badge>
                </div>
                <div className="space-y-2">{group.items.map(s => <SignalCard key={s.id} signal={s} />)}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add signal dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Add Research Signal</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Signal type</Label>
                <Select value={form.signal_type} onValueChange={v => setForm(p => ({ ...p, signal_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="external">External research</SelectItem>
                    <SelectItem value="cross_client">Cross-client pattern</SelectItem>
                    <SelectItem value="internal">Internal pattern</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Relevance</Label>
                <Select value={form.relevance_level} onValueChange={v => setForm(p => ({ ...p, relevance_level: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">High — Test this</SelectItem>
                    <SelectItem value="medium">Medium — Interesting</SelectItem>
                    <SelectItem value="observation">Observation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Loss aversion outperforms reward framing in SaaS adoption" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Description *</Label>
              <Textarea rows={3} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What's the finding? Include specific numbers or observations." />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Source</Label>
              <Input value={form.source} onChange={e => setForm(p => ({ ...p, source: e.target.value }))} placeholder="e.g. HBS study, 2024 · or · Cross-client: Multiplier + Do Stuff" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cosmo's take (your interpretation)</Label>
              <Textarea rows={2} value={form.cosmo_take} onChange={e => setForm(p => ({ ...p, cosmo_take: e.target.value }))} placeholder="How could this change our approach? Which clients should we test it with?" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={addSignal} disabled={addSaving}>{addSaving ? 'Adding…' : 'Add Signal'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
